import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CATEGORY_LIST, STATUS_LIST, CATEGORIES, STATUSES, TEMPERATURE_LIST, TEMPERATURES, formatCurrencyInput, parseCurrencyInput, fmtMoney } from "@/lib/leadMeta";
import { INDUSTRIES } from "@/lib/industries";
import { formatPhone } from "@/lib/phone";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { US_STATES, CITIES_BY_STATE } from "@/lib/usGeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InsuranceTypePicker } from "@/components/InsuranceTypePicker";
import { Loader2 } from "lucide-react";

const ignorePac = (e) => {
  const t = e.detail?.originalEvent?.target || e.target;
  if (t?.closest?.(".pac-container")) e.preventDefault();
};

export const EditLeadDialog = ({ lead, onClose, onDone }) => {
  const industryKnown = !lead.industry || INDUSTRIES.includes(lead.industry);
  const [form, setForm] = useState({
    name: lead.name || "", company: lead.company || "", email: lead.email || "", phone: lead.phone || "",
    address: lead.address || "", address2: lead.address2 || "", city: lead.city || "", state: lead.state || "", zip: lead.zip || "",
    lat: lead.lat ?? null, lng: lead.lng ?? null,
    insurance_types: lead.insurance_types || [lead.insurance_type || "life"], status: lead.status || "new",
    primary_type: lead.primary_type || lead.insurance_type || (lead.insurance_types && lead.insurance_types[0]) || "life",
    premium_estimate: lead.premium_estimate ?? "", commission_pct: lead.commission_pct ?? "", notes: lead.notes || "",
    industry: lead.industry ? (industryKnown ? lead.industry : "Other") : "", employees: lead.employees ?? "", temperature: lead.temperature || "",
  });
  const [loading, setLoading] = useState(false);
  const [industryOther, setIndustryOther] = useState(industryKnown ? "" : (lead.industry || ""));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Lead name is required");
    setLoading(true);
    try {
      await api.patch(`/leads/${lead.lead_id}`, {
        ...form, premium_estimate: form.premium_estimate === "" ? null : Number(form.premium_estimate),
        commission_pct: form.commission_pct === "" || form.commission_pct == null ? null : Number(form.commission_pct),
        employees: form.employees === "" || form.employees == null ? null : Number(form.employees),
        temperature: form.temperature || null, industry: form.industry === "Other" ? (industryOther.trim() || "Other") : (form.industry || ""),
      });
      toast.success("Lead updated");
      onDone && onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="edit-lead-dialog" onInteractOutside={ignorePac} onPointerDownOutside={ignorePac}>
        <DialogHeader><DialogTitle className="font-heading">Edit lead</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Full name</Label><Input data-testid="edit-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /></div>
            <div className="col-span-2"><Label>Company name</Label><Input data-testid="edit-company-input" value={form.company} onChange={set("company")} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={set("email")} className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))} className="mt-1.5" /></div>
            <div className="col-span-2"><Label>Address</Label>
              <AddressAutocomplete value={form.address} onChange={set("address")} onSelect={(a) => setForm((f) => ({ ...f, ...a }))} className="mt-1.5" />
            </div>
            <div className="col-span-2"><Label>Address 2 <span className="text-xs text-muted-foreground">(apt, suite, unit)</span></Label>
              <Input data-testid="edit-address2-input" value={form.address2} onChange={set("address2")} className="mt-1.5" placeholder="Apt / Suite / Unit" />
            </div>
            <div><Label>City</Label>
              <Input list="edit-city-list" value={form.city} onChange={set("city")} className="mt-1.5" placeholder="Start typing…" />
              <datalist id="edit-city-list">{(CITIES_BY_STATE[form.state] || []).map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>State</Label>
                <Select value={form.state || ""} onValueChange={(v) => setForm((f) => ({ ...f, state: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="edit-state-input"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent className="max-h-64">{US_STATES.map(([code, name]) => <SelectItem key={code} value={code}>{code} — {name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>ZIP</Label><Input value={form.zip} onChange={set("zip")} className="mt-1.5" /></div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Insurance type(s)</Label>
              <InsuranceTypePicker value={form.insurance_types} primary={form.primary_type}
                onChange={(v) => setForm((f) => ({ ...f, insurance_types: v, primary_type: v.includes(f.primary_type) ? f.primary_type : (v[0] || "") }))}
                onPrimaryChange={(k) => setForm((f) => ({ ...f, primary_type: k }))} />
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger className="mt-1.5" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_LIST.map((k) => <SelectItem key={k} value={k}>{STATUSES[k].label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Industry</Label>
              <Select value={form.industry || ""} onValueChange={(v) => { setForm((f) => ({ ...f, industry: v })); if (v !== "Other") setIndustryOther(""); }}>
                <SelectTrigger className="mt-1.5" data-testid="edit-industry-select"><SelectValue placeholder="Select industry" /></SelectTrigger>
                <SelectContent className="max-h-64">{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
              {form.industry === "Other" && (
                <Input className="mt-2" placeholder="Type the industry" value={industryOther} onChange={(e) => setIndustryOther(e.target.value)} data-testid="edit-industry-other" />
              )}
            </div>
            <div><Label>Number of employees</Label><Input data-testid="edit-employees-input" type="number" min="0" inputMode="numeric" value={form.employees} onChange={set("employees")} className="mt-1.5" placeholder="e.g. 25" /></div>
            <div><Label>Lead temperature</Label>
              <Select value={form.temperature || ""} onValueChange={set("temperature")}>
                <SelectTrigger className="mt-1.5" data-testid="edit-temperature-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{TEMPERATURE_LIST.map((k) => <SelectItem key={k} value={k}>{TEMPERATURES[k].label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Premium estimate ($/yr)</Label><Input data-testid="edit-premium-input" inputMode="numeric" placeholder="$1,200" value={formatCurrencyInput(form.premium_estimate)} onChange={(e) => setForm((f) => ({ ...f, premium_estimate: parseCurrencyInput(e.target.value) }))} className="mt-1.5" /></div>
            <div><Label>Commission %</Label>
              <div className="relative mt-1.5">
                <Input data-testid="edit-commission-pct-input" type="number" min="0" max="100" step="0.1" inputMode="decimal" placeholder="10" value={form.commission_pct} onChange={(e) => setForm((f) => ({ ...f, commission_pct: e.target.value }))} className="pr-7" />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
              </div>
            </div>
            <div className="col-span-2 flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5">
              <span className="text-sm font-medium">Projected commission</span>
              <span className="font-mono font-semibold text-primary" data-testid="edit-projected-commission">{fmtMoney((Number(form.premium_estimate) || 0) * (Number(form.commission_pct) || 0) / 100)}</span>
            </div>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} className="mt-1.5" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading} data-testid="cancel-edit-button">Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="update-lead-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
